import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Stack,
  Paper,
  Chip,
  Button,
  Typography,
  Tooltip,
  TextField,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  useTheme,
  alpha,
} from "@mui/material";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import Nodata from "../../../assets/errors/nodata.png";

import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

const ALL = "__all__";
const UNDATED = "__undated__";

// Conditions excluded from the audit population, matching the Fixed Asset Register.
const NON_CAPITAL_CONDITIONS = ["discarded", "damaged", "refurbished"];

// Any non-zero difference means the FAR and the accounting records disagree and
// the line needs auditing. There is deliberately no tolerance band.
// Orangered. Must stay a hex literal: MUI's alpha() cannot parse CSS colour
// keywords and throws "Unsupported OrangeRed color" at render time.
const NEEDS_AUDIT_COLOR = "#FF4500";

/* ─── Helpers ──────────────────────────────────────────────────────────── */

const num = (v) => parseFloat(v) || 0;

const money = (v) =>
  `N$ ${num(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const compactMoney = (v) =>
  `N$ ${num(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isCapitalised = (item) =>
  !NON_CAPITAL_CONDITIONS.includes(
    String(item?.product_condition || "").toLowerCase()
  );

const hasDiscrepancy = (record) => num(record?.difference) !== 0;

/**
 * The depreciation month is the month the far_audit row was created - each
 * monthly depreciation run writes one record per asset, so createDate is the
 * authoritative period. year_end is only a fallback for legacy rows written
 * before createDate existed.
 */
const monthKeyOf = (record) => {
  for (const raw of [record?.createDate, record?.year_end]) {
    if (!raw) continue;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) continue;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }
  return UNDATED;
};

// Key for a given calendar month, matching the shape monthKeyOf produces.
const monthKeyFor = (year, month) =>
  `${year}-${String(month + 1).padStart(2, "0")}`;

const monthLabel = (key) => {
  if (key === UNDATED) return "Undated";
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
};

// "Undated" always sorts last, everything else chronologically.
const compareMonthKeys = (a, b) => {
  if (a === UNDATED) return 1;
  if (b === UNDATED) return -1;
  return a.localeCompare(b);
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Absolute month index so windows can be compared across year boundaries.
const monthIndex = (year, month) => year * 12 + month;

/* ─── Presentational components ────────────────────────────────────────── */
// Module scope on purpose: a component declared inside AssetsAudit would be a
// new type on every render, remounting these subtrees on each keystroke.

const StatCard = ({ ui, icon, label, value, sub, tone }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.25,
      height: "100%",
      borderRadius: 3,
      bgcolor: ui.surface,
      border: `1px solid ${ui.border}`,
      boxShadow: ui.shadow,
      borderLeft: `4px solid ${tone}`,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          color: tone,
          bgcolor: alpha(tone, 0.14),
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="h6"
        sx={{
          letterSpacing: 0.6,
          textTransform: "uppercase",
          fontWeight: 700,
          color: ui.textSecondary,
        }}
      >
        {label}
      </Typography>
    </Stack>
    <Typography
      variant="h3"
      sx={{ fontWeight: 800, color: ui.textPrimary, lineHeight: 1.2 }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography variant="h6" sx={{ mt: 0.35, color: ui.textSecondary }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

const DetailRow = ({ ui, label, value, color }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="h6"
      sx={{
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: ui.textSecondary,
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h5"
      sx={{
        fontWeight: 600,
        color: color || ui.textPrimary,
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

/* ─────────────────────────────────────────────────────────────────────── */

const AssetsAudit = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { getData } = useAuth();

  const [assets, setAssets] = useState([]);
  const [auditRecords, setAuditRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState(ALL);
  const [categoryFilter, setCategoryFilter] = useState(ALL);
  const [yearEndFilter, setYearEndFilter] = useState(ALL);
  const [varianceFilter, setVarianceFilter] = useState(ALL);
  const [selectedRow, setSelectedRow] = useState(null);

  /* ---------------- Derived design tokens ---------------- */
  const ui = useMemo(() => {
    const accent = colors.teal[500];
    const accent2 = colors.newTeal[500];
    return {
      accent,
      accent2,
      pageBg: isDark ? colors.dark[800] : "#e9ebef",
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
      shadow: isDark
        ? "0 14px 34px -22px rgba(0,0,0,0.8)"
        : "0 14px 34px -24px rgba(16,24,74,0.35)",
    };
  }, [colors, isDark]);

  /* ---------------- Data ---------------- */
  const fetchData = useCallback(async () => {
    try {
      const [inventoryResponse, auditResponse] = await Promise.all([
        getData("proccess.php", "inventory"),
        getData("proccess.php", "far_audit"),
      ]);

      if (inventoryResponse.success) {
        const rows = Array.isArray(inventoryResponse.data)
          ? inventoryResponse.data
          : [];
        setAssets(rows.filter(isCapitalised));
      } else {
        toast.error(inventoryResponse.message || "Failed to load asset data");
      }

      if (auditResponse.success) {
        setAuditRecords(
          Array.isArray(auditResponse.data) ? auditResponse.data : []
        );
      } else {
        toast.error(auditResponse.message || "Failed to load FAR audit data");
      }
    } catch (err) {
      toast.error("Network error: Failed to fetch audit data");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [getData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const assetById = useMemo(() => {
    const map = new Map();
    assets.forEach((a) => map.set(String(a.id), a));
    return map;
  }, [assets]);

  /**
   * One flattened row per audit record, joined to its asset. Ids are compared as
   * strings — the API returns them inconsistently typed, and the original `===`
   * comparison silently failed to match.
   */
  const rows = useMemo(
    () =>
      auditRecords.map((record) => {
        const asset = assetById.get(String(record.asset_id));
        const monthKey = monthKeyOf(record);
        return {
          ...record,
          _asset: asset,
          sku: asset?.sku || `#${record.asset_id}`,
          description: asset?.description || "",
          category: asset?.category || "Uncategorized",
          location: asset?.location || "",
          costprice: asset?.costprice,
          monthKey,
          monthName: monthLabel(monthKey),
          flagged: hasDiscrepancy(record),
        };
      }),
    [auditRecords, assetById]
  );

  /* ---------------- Filter options ---------------- */
  const filterOptions = useMemo(() => {
    const months = new Set();
    const categories = new Set();
    const yearEnds = new Set();
    rows.forEach((r) => {
      months.add(r.monthKey);
      if (r.category) categories.add(r.category);
      if (r.year_end) yearEnds.add(String(r.year_end));
    });
    // Years that actually have depreciation runs recorded against them.
    const years = new Set();
    rows.forEach((r) => {
      if (r.monthKey === UNDATED) return;
      years.add(Number(r.monthKey.slice(0, 4)));
    });
    if (years.size === 0) years.add(new Date().getFullYear());

    return {
      months: [...months].sort(compareMonthKeys),
      categories: [...categories].sort((a, b) => a.localeCompare(b)),
      yearEnds: [...yearEnds].sort(),
      years: [...years].sort((a, b) => b - a),
    };
  }, [rows]);

  const activeFilterCount = [
    monthFilter,
    categoryFilter,
    yearEndFilter,
    varianceFilter,
  ].filter((v) => v !== ALL).length;

  const clearFilters = () => {
    setMonthFilter(ALL);
    setCategoryFilter(ALL);
    setYearEndFilter(ALL);
    setVarianceFilter(ALL);
  };

  /* ---------------- Filtering ---------------- */
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return rows
      .filter((r) => monthFilter === ALL || r.monthKey === monthFilter)
      .filter((r) => categoryFilter === ALL || r.category === categoryFilter)
      .filter(
        (r) => yearEndFilter === ALL || String(r.year_end) === yearEndFilter
      )
      .filter((r) => {
        if (varianceFilter === ALL) return true;
        return varianceFilter === "flagged" ? r.flagged : !r.flagged;
      })
      .filter((r) => {
        if (!q) return true;
        return [r.sku, r.description, r.category, r.location, r.year_end, r.createDate]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
      .sort(
        (a, b) =>
          compareMonthKeys(a.monthKey, b.monthKey) ||
          String(a.sku).localeCompare(String(b.sku))
      );
  }, [rows, search, monthFilter, categoryFilter, yearEndFilter, varianceFilter]);

  const totals = useMemo(() => {
    const sum = (key) => visibleRows.reduce((acc, r) => acc + num(r[key]), 0);
    return {
      records: visibleRows.length,
      months: new Set(visibleRows.map((r) => r.monthKey)).size,
      depreciation: sum("depreciation"),
      monthlyDepreciation: sum("depriciable_ammount"),
      accumulated: sum("accumulated_depr"),
      netbook: sum("netbook_value"),
      difference: sum("difference"),
      flagged: visibleRows.filter((r) => r.flagged).length,
    };
  }, [visibleRows]);

  /* ---------------- Export ---------------- */
  // Your original report structure, unchanged: the same 15 green main-asset
  // columns followed by the same 9 purple FAR-audit columns, the same header,
  // legend, totals rows and audit notes. The only difference is that sections
  // are grouped by depreciation month instead of by category.
  const exportToExcel = () => {
    if (visibleRows.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      // A full calendar year: January through December each get their own table,
      // including months where nothing is being depreciated. An asset appears in
      // every month it is actively being written down, not just once.
      const nowIdx = monthIndex(
        new Date().getFullYear(),
        new Date().getMonth()
      );

      const schedule = MONTH_NAMES.map((name, month) => ({
        month,
        name,
        label: `${name} ${yearFilter}`,
        future: monthIndex(yearFilter, month) > nowIdx,
        rows: visibleRows.filter(
          (row) => row.monthKey === monthKeyFor(yearFilter, month)
        ),
      }));

      const mainAssetFields = [
        { key: "id", label: "ID", width: 10, type: "main" },
        { key: "sku", label: "SKU", width: 15, type: "main" },
        { key: "description", label: "Description", width: 30, type: "main" },
        { key: "lifeSpanPeriod", label: "Useful Life (Years)", width: 15, type: "main" },
        { key: "createDate", label: "Run Date", width: 12, type: "main" },
        { key: "costprice", label: "Cost Price (N$)", width: 12, type: "main" },
        { key: "additions", label: "Additions (N$)", width: 12, type: "main" },
        { key: "actualValue", label: "Closing Cost (N$)", width: 12, type: "main" },
        { key: "salvageValue", label: "Residual Value (N$)", width: 12, type: "main" },
        { key: "depreciationValue", label: "Depreciatable Amount (N$)", width: 12, type: "main" },
        { key: "opening_depr", label: "Opening Depreciation (N$)", width: 12, type: "main" },
        { key: "current_period_depr", label: "Current Period Depreciation (N$)", width: 12, type: "main" },
        { key: "accumulated_depr", label: "Accumulated Depreciation (N$)", width: 12, type: "main" },
        { key: "carrying_value", label: "Carrying Value PY (N$)", width: 12, type: "main" },
        { key: "book_value", label: "Book Value (N$)", width: 12, type: "main" },
      ];

      const farAuditFields = [
        { key: "year_end", label: "Year End", width: 12, type: "far" },
        { key: "used_months", label: "Used Months", width: 10, type: "far" },
        { key: "months_left_to_use", label: "Months Left to Use", width: 12, type: "far" },
        { key: "depriciable", label: "Depreciable Amount (N$)", width: 12, type: "far" },
        { key: "depriciable_ammount", label: "Monthly Depreciation (N$)", width: 12, type: "far" },
        { key: "depreciation", label: "Total Depreciation (N$)", width: 12, type: "far" },
        { key: "far_accumulated_depr", label: "Accumulated Depreciation (N$)", width: 12, type: "far" },
        { key: "netbook_value", label: "Net Book Value (N$)", width: 12, type: "far" },
        { key: "difference", label: "Difference (N$)", width: 12, type: "far" },
      ];

      const allFields = [...mainAssetFields, ...farAuditFields];

      const formatCurrency = (value) => {
        if (!value && value !== 0) return "-";
        return `N$ ${parseFloat(value).toFixed(2)}`;
      };
      const formatDate = (date) => {
        if (!date) return "-";
        return new Date(date).toLocaleDateString();
      };
      const formatNumber = (value) => {
        if (!value && value !== 0) return "-";
        return parseFloat(value).toFixed(2);
      };

      // Asset-level totals are taken once over the distinct filtered records.
      const totalAsset = (field) =>
        visibleRows.reduce((sum, r) => sum + num(r._asset?.[field]), 0);
      const totalAudit = (field) =>
        visibleRows.reduce((sum, r) => sum + num(r[field]), 0);

      const grandTotalCost = totalAsset("costprice");
      const grandTotalAdditions = totalAsset("additions");
      const grandTotalCurrent = totalAsset("actualValue");
      const grandTotalDepreciable = totalAsset("depreciationValue");
      const grandTotalAccDepr = totalAsset("accumulated_depr");
      const grandTotalNetBookValue = totalAudit("netbook_value");

      // Sum of each month's depreciation charge across the twelve months.
      let yearDepreciationCharge = 0;

      let htmlContent = `
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            * { font-family: Calibri, sans-serif; }

            .main-header {
              background-color: #1B4F72;
              color: white;
              font-size: 14px;
              font-weight: bold;
              text-align: center;
              padding: 12px;
            }

            .category-header {
              background-color: #2874A6;
              color: white;
              font-size: 14px;
              font-weight: bold;
              text-align: left;
              padding: 10px 12px;
            }

            .main-table-header {
              background-color: #27AE60;
              color: white;
              font-size: 12px;
              font-weight: bold;
              text-align: center;
              padding: 8px;
              border-right: 1px solid #1E8449;
            }

            .far-table-header {
              background-color: #8E44AD;
              color: white;
              font-size: 12px;
              font-weight: bold;
              text-align: center;
              padding: 8px;
              border-right: 1px solid #6C3483;
            }

            .section-divider {
              background-color: #F39C12;
              color: white;
              font-size: 12px;
              font-weight: bold;
              text-align: center;
              padding: 6px;
            }

            .data-row { background-color: #FFFFFF; font-size: 12px; }
            .data-row-alt { background-color: #F2F3F4; font-size: 12px; }
            .far-data-row { background-color: #F4ECF7; font-size: 12px; }
            .far-data-row-alt { background-color: #E8DAEF; font-size: 12px; }

            td, th {
              border: 1px solid #BDC3C7;
              padding: 8px 10px;
              text-align: left;
            }

            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .text-left { text-align: left; }

            .main-asset-col { background-color: #FFFFFF; }
            .far-audit-col { background-color: #F9EBEA; }

            .summary-row {
              background-color: #D5DBDB;
              font-weight: bold;
              font-size: 13px;
            }
            .summary-row td { font-weight: bold; }

            .category-totals-row {
              background-color: #F0F3F4;
              font-weight: bold;
              font-size: 13px;
              border-top: 2px solid #1B4F72;
            }
            .category-totals-row td {
              font-weight: bold;
              background-color: #E8F4F8;
            }

            .asset-count {
              background-color: #F39C12;
              color: white;
              padding: 3px 10px;
              border-radius: 12px;
              font-size: 12px;
              margin-left: 10px;
            }

            table {
              border-collapse: collapse;
              width: 100%;
              margin-bottom: 20px;
            }

            .col-separator { border-left: 2px solid #E74C3C; }
            .bold-total { font-weight: bold; }

            /* Any line whose difference is not zero must be audited. */
            .needs-audit td {
              background-color: #FF4500;
              color: #FFFFFF;
              font-weight: bold;
              font-size: 12px;
            }
          </style>
        </head>
        <body>
          <h2 style="color: #1B4F72; margin-bottom: 5px; font-size: 18px;">INVENTORY &amp; FAR AUDIT REPORT</h2>
          <p style="color: #7F8C8D; margin-top: 0; margin-bottom: 20px; font-size: 12px;">
            Generated: ${new Date().toLocaleString()} | Depreciation schedule for ${yearFilter} | ${visibleRows.length} asset(s) in scope
          </p>

          <div style="margin-bottom: 15px; padding: 10px; background-color: #EBF5FB; border-left: 4px solid #3498DB; font-size: 12px;">
            <span style="display: inline-block; width: 20px; height: 20px; background-color: #27AE60; margin-right: 10px;"></span>
            <strong style="margin-right: 20px;">Main Asset Data</strong>
            <span style="display: inline-block; width: 20px; height: 20px; background-color: #8E44AD; margin-right: 10px;"></span>
            <strong style="margin-right: 20px;">FAR Audit Data</strong>
            <span style="display: inline-block; width: 20px; height: 20px; background-color: #FF4500; margin-right: 10px;"></span>
            <strong>Difference not zero &mdash; needs audit</strong>
          </div>
      `;

      for (const period of schedule) {
        const monthRows = period.rows;

        // Months with no depreciation still appear, so the year reads as twelve
        // consecutive periods rather than a gapped list.
        if (monthRows.length === 0) {
          htmlContent += `
            <table>
              <tr>
                <td colspan="${allFields.length}" class="category-header">
                  ${period.label.toUpperCase()}
                  <span class="asset-count">${period.future ? "Not yet reached" : "0 Assets"}</span>
                 </td>
              </tr>
              <tr class="data-row">
                <td colspan="${allFields.length}" style="text-align: center; font-size: 12px; color: #7F8C8D; padding: 14px;">
                  ${
                    period.future
                      ? "This period has not occurred yet - no depreciation has been charged."
                      : "No assets were being depreciated in this month."
                  }
                </td>
              </tr>
            </table><br/>
          `;
          continue;
        }

        const sumRows = (field) =>
          monthRows.reduce((sum, r) => sum + num(r[field]), 0);
        const sumAsset = (field) =>
          monthRows.reduce((sum, r) => sum + num(r._asset?.[field]), 0);

        const monthTotalCost = sumAsset("costprice");
        const monthTotalAdditions = sumAsset("additions");
        const monthTotalCurrent = sumAsset("actualValue");
        const monthTotalSalvage = sumAsset("salvageValue");
        const monthTotalDepreciable = sumAsset("depreciationValue");
        const monthTotalOpeningDepr = sumAsset("opening_depr");
        const monthTotalCurrentDepr = sumAsset("current_period_depr");
        const monthTotalAccDepr = sumAsset("accumulated_depr");
        const monthTotalCarryingValue = sumAsset("carrying_value");
        const monthTotalBookValue = sumAsset("book_value");

        const monthTotalDepreciableAmount = sumRows("depriciable");
        const monthTotalMonthlyDepr = sumRows("depriciable_ammount");
        const monthTotalDepreciation = sumRows("depreciation");
        const monthTotalFarAccDepr = sumRows("accumulated_depr");
        const monthTotalNetBookValue = sumRows("netbook_value");
        const monthTotalDifference = sumRows("difference");

        // Only the monthly charge accumulates across the year. Asset-level
        // figures are totalled once, after the loop, because an asset appears in
        // every month it depreciates and would otherwise be counted many times.
        yearDepreciationCharge += monthTotalMonthlyDepr;

        htmlContent += `
          <table>
            <tr>
              <td colspan="${allFields.length}" class="category-header">
                ${period.label.toUpperCase()}
                <span class="asset-count">${monthRows.length} Assets</span>
               </td>
            </tr>

            <tr>
        `;

        mainAssetFields.forEach((field) => {
          htmlContent += `<th class="main-table-header">${field.label}</th>`;
        });

        farAuditFields.forEach((field) => {
          htmlContent += `<th class="far-table-header">${field.label}</th>`;
        });

        htmlContent += `</tr>`;

        monthRows.forEach((row, index) => {
          const asset = row._asset || {};
          const rowClass = row.flagged
            ? "needs-audit"
            : index % 2 === 0
            ? "data-row"
            : "data-row-alt";
          // Excel ignores much class-based styling, so the colour is inlined too.
          const rowStyle = row.flagged
            ? `background-color: ${NEEDS_AUDIT_COLOR}; color: #FFFFFF; font-weight: bold;`
            : "";

          htmlContent += `<tr class="${rowClass}">`;

          mainAssetFields.forEach((field) => {
            let value = "";
            let cellClass = "text-left";

            switch (field.key) {
              case "id":
                value = asset.id || row.asset_id || "-";
                cellClass = "text-center";
                break;
              case "sku":
                value = row.sku || "-";
                break;
              case "description":
                value = asset.description || "-";
                break;
              case "lifeSpanPeriod":
                value = formatNumber(asset.lifeSpanPeriod);
                cellClass = "text-center";
                break;
              case "createDate":
                // From the audit record, not the asset - this is what the month
                // grouping is keyed on.
                value = formatDate(row.createDate);
                cellClass = "text-center";
                break;
              default:
                value = formatCurrency(asset[field.key]);
                cellClass = "text-right";
            }

            htmlContent += `<td class="${cellClass}" style="font-size: 12px; ${rowStyle}">${value}</td>`;
          });

          farAuditFields.forEach((field) => {
            let value = "";
            let cellClass = "text-left";
            let extraStyle = "";

            switch (field.key) {
              case "year_end":
                value = row.year_end || "-";
                cellClass = "text-center";
                break;
              case "used_months":
                value = formatNumber(row.used_months);
                cellClass = "text-center";
                break;
              case "months_left_to_use":
                value = formatNumber(row.months_left_to_use);
                cellClass = "text-center";
                break;
              case "far_accumulated_depr":
                value = formatCurrency(row.accumulated_depr);
                cellClass = "text-right";
                break;
              case "difference":
                value = formatCurrency(row.difference);
                cellClass = "text-right";
                if (row.flagged) {
                  value = `<strong>${value}</strong>`;
                }
                break;
              default:
                value = formatCurrency(row[field.key]);
                cellClass = "text-right";
            }

            htmlContent += `<td class="${cellClass}" style="font-size: 12px; ${rowStyle}${extraStyle}">${value}</td>`;
          });

          htmlContent += `</tr>`;
        });

        const monthNeedsAudit = monthTotalDifference !== 0;
        const totalRowStyle = monthNeedsAudit
          ? `background-color: ${NEEDS_AUDIT_COLOR}; color: #FFFFFF;`
          : "background-color: #E8F4F8;";

        htmlContent += `
          <tr class="category-totals-row" style="${totalRowStyle} font-weight: bold;">
            <td colspan="3" style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">MONTH TOTALS &rarr;${
              monthNeedsAudit ? " NEEDS AUDIT" : ""
            }</td>
            <td style="text-align: center; font-weight: bold; font-size: 13px; ${totalRowStyle}">-</td>
            <td style="text-align: center; font-weight: bold; font-size: 13px; ${totalRowStyle}">-</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalCost)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalAdditions)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalCurrent)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalSalvage)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalDepreciable)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalOpeningDepr)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalCurrentDepr)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalAccDepr)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalCarryingValue)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalBookValue)}</td>
            <td style="text-align: center; font-weight: bold; font-size: 13px; ${totalRowStyle}">-</td>
            <td style="text-align: center; font-weight: bold; font-size: 13px; ${totalRowStyle}">-</td>
            <td style="text-align: center; font-weight: bold; font-size: 13px; ${totalRowStyle}">-</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalDepreciableAmount)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalMonthlyDepr)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalDepreciation)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalFarAccDepr)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalNetBookValue)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px; ${totalRowStyle}">${formatCurrency(monthTotalDifference)}</td>
          </tr>
        `;

        htmlContent += `</table><br/>`;
      }

      htmlContent += `
        <table style="margin-top: 20px;">
          <tr style="background-color: #1B4F72; color: white; font-weight: bold;">
            <td colspan="${allFields.length}" style="text-align: center; font-size: 14px; padding: 12px;">
              GRAND TOTALS - ${yearFilter}
            </td>
          </tr>
          <tr style="background-color: #E8E8E8; font-weight: bold;">
            <td colspan="4" style="text-align: right; font-weight: bold; font-size: 13px;">Total Cost Value:</td>
            <td colspan="3" style="font-weight: bold; font-size: 13px;">${formatCurrency(grandTotalCost)}</td>
            <td colspan="${allFields.length - 7}" style="font-weight: bold;">&nbsp;</td>
          </tr>
          <tr style="background-color: #F5F5F5; font-weight: bold;">
            <td colspan="4" style="text-align: right; font-weight: bold; font-size: 13px;">Total Additions:</td>
            <td colspan="3" style="font-weight: bold; font-size: 13px;">${formatCurrency(grandTotalAdditions)}</td>
            <td colspan="${allFields.length - 7}" style="font-weight: bold;">&nbsp;</td>
          </tr>
          <tr style="background-color: #E8E8E8; font-weight: bold;">
            <td colspan="4" style="text-align: right; font-weight: bold; font-size: 13px;">Total Current Value:</td>
            <td colspan="3" style="font-weight: bold; font-size: 13px;">${formatCurrency(grandTotalCurrent)}</td>
            <td colspan="${allFields.length - 7}" style="font-weight: bold;">&nbsp;</td>
          </tr>
          <tr style="background-color: #F5F5F5; font-weight: bold;">
            <td colspan="4" style="text-align: right; font-weight: bold; font-size: 13px;">Total Depreciable Amount:</td>
            <td colspan="3" style="font-weight: bold; font-size: 13px;">${formatCurrency(grandTotalDepreciable)}</td>
            <td colspan="${allFields.length - 7}" style="font-weight: bold;">&nbsp;</td>
          </tr>
          <tr style="background-color: #E8E8E8; font-weight: bold;">
            <td colspan="4" style="text-align: right; font-weight: bold; font-size: 13px;">Total Accumulated Depreciation:</td>
            <td colspan="3" style="font-weight: bold; font-size: 13px;">${formatCurrency(grandTotalAccDepr)}</td>
            <td colspan="${allFields.length - 7}" style="font-weight: bold;">&nbsp;</td>
          </tr>
          <tr style="background-color: #D5DBDB; font-weight: bold;">
            <td colspan="4" style="text-align: right; font-weight: bold; font-size: 13px;">Net Book Value (Total):</td>
            <td colspan="3" style="font-weight: bold; font-size: 13px;">${formatCurrency(grandTotalNetBookValue)}</td>
            <td colspan="${allFields.length - 7}" style="font-weight: bold;">&nbsp;</td>
          </tr>
          <tr style="background-color: #F39C12; color: white; font-weight: bold;">
            <td colspan="4" style="text-align: right; font-weight: bold; font-size: 13px;">Depreciation Charged in ${yearFilter} (12 months):</td>
            <td colspan="3" style="font-weight: bold; font-size: 13px;">${formatCurrency(yearDepreciationCharge)}</td>
            <td colspan="${allFields.length - 7}" style="font-weight: bold;">&nbsp;</td>
          </tr>
        </table>

        <table style="margin-top: 20px;">
          <tr style="background-color: #1B4F72; color: white; font-weight: bold;">
            <td colspan="4" style="text-align: center; font-size: 14px; padding: 10px;">MONTHLY SUMMARY - ${yearFilter}</td>
          </tr>
          <tr class="col-header">
            <th class="main-table-header">Month</th>
            <th class="main-table-header">Assets Depreciating</th>
            <th class="far-table-header">Monthly Depreciation (N$)</th>
            <th class="far-table-header">Closing Net Book Value (N$)</th>
          </tr>
          ${schedule
            .map(
              (period, i) => `
            <tr class="${i % 2 === 0 ? "data-row" : "data-row-alt"}">
              <td>${period.label}${period.future ? " (not yet reached)" : ""}</td>
              <td class="text-center">${period.rows.length}</td>
              <td class="text-right">${formatCurrency(
                period.rows.reduce((sum, r) => sum + num(r.depriciable_ammount), 0)
              )}</td>
              <td class="text-right">${formatCurrency(
                period.rows.reduce((sum, r) => sum + num(r.netbook_value), 0)
              )}</td>
            </tr>`
            )
            .join("")}
          <tr class="category-totals-row" style="background-color: #E8F4F8; font-weight: bold;">
            <td style="font-weight: bold;">TOTAL</td>
            <td class="text-center" style="font-weight: bold;">-</td>
            <td class="text-right" style="font-weight: bold;">${formatCurrency(yearDepreciationCharge)}</td>
            <td class="text-right" style="font-weight: bold;">-</td>
          </tr>
        </table>

        <div style="margin-top: 30px; padding: 12px; background-color: #F9EBEA; border-left: 4px solid #E74C3C; font-size: 11px;">
          <strong>Audit Notes:</strong><br/>
          <span style="color: #27AE60;">&#9632;</span> <strong>Green headers</strong> = Main Asset Data (Accounting records)<br/>
          <span style="color: #8E44AD;">&#9632;</span> <strong>Purple headers</strong> = FAR Audit Data (Audit records)<br/>
          &bull; <span style="background-color: #FF4500; color: #FFFFFF; font-weight: bold; padding: 1px 6px;">Orangered lines</span> have a non-zero difference between the FAR and the accounting records and <strong>must be audited</strong><br/>
          &bull; A month total shown in orangered means that month does not reconcile overall<br/>
          &bull; All values are in Namibian Dollars (N$)<br/>
          &bull; Report generated on: ${new Date().toLocaleString()}<br/>
          &bull; The report covers all twelve months of ${yearFilter}; months with no depreciation are shown empty<br/>
          &bull; A record falls in the month its depreciation run was recorded (far_audit createDate); year end is used only for legacy rows with no createDate<br/>
          &bull; Nothing is projected forward - a month is populated only once its depreciation run has actually been written<br/>
          &bull; Asset-level totals (cost, additions, closing cost, accumulated depreciation, net book value) are counted once per asset; only the monthly charge accumulates across the year<br/>
          &bull; <strong>Note: Bold rows represent month totals for each column</strong>
        </div>
      </body></html>`;

      const blob = new Blob([htmlContent], {
        type: "application/vnd.ms-excel",
      });

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `inventory_far_audit_${yearFilter}.xls`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(
        `Exported the ${yearFilter} depreciation schedule: ${visibleRows.length} asset(s) across 12 months`
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };

  /* ---------------- Shared style helpers ---------------- */
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      color: ui.textPrimary,
      bgcolor: ui.surfaceAlt,
      "& fieldset": { borderColor: ui.border },
      "&:hover fieldset": { borderColor: alpha(ui.accent, 0.5) },
      "&.Mui-focused fieldset": { borderColor: ui.accent },
    },
    "& .MuiInputLabel-root": { color: ui.textSecondary },
    "& .MuiInputLabel-root.Mui-focused": { color: ui.accent },
  };

  const primaryBtn = {
    px: 2.75,
    py: 1.1,
    borderRadius: 2,
    fontWeight: 700,
    textTransform: "none",
    color: "#ffffff",
    background: ui.gradient,
    boxShadow: `0 10px 24px -12px ${alpha(ui.accent, 0.9)}`,
    "&:hover": { filter: "brightness(1.06)" },
  };

  const moneyCell = (value, tone) => (
    <Typography
      variant="h6"
      sx={{
        width: "100%",
        textAlign: "right",
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        color: num(value) !== 0 ? tone : ui.textSecondary,
      }}
    >
      {num(value).toLocaleString("en-US", { minimumFractionDigits: 2 })}
    </Typography>
  );

  /* ---------------- Columns ---------------- */
  const columns = useMemo(
    () => [
      {
        field: "monthName",
        headerName: "Depreciation month",
        width: 170,
        renderCell: (p) => (
          <Chip
            size="small"
            icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 14 }} />}
            label={p.row.monthName}
            sx={{
              height: 22,
              fontWeight: 700,
              fontSize: "0.68rem",
              color: p.row.monthKey === UNDATED ? ui.textSecondary : ui.accent,
              bgcolor: alpha(
                p.row.monthKey === UNDATED ? ui.textSecondary : ui.accent,
                0.14
              ),
              "& .MuiChip-icon": { color: "inherit" },
            }}
          />
        ),
      },
      {
        field: "sku",
        headerName: "SKU",
        width: 130,
        renderCell: (p) => (
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: ui.accent2 }}
            noWrap
          >
            {p.row.sku}
          </Typography>
        ),
      },
      { field: "description", headerName: "Description", width: 230 },
      { field: "category", headerName: "Category", width: 150 },
      {
        field: "createDate",
        headerName: "Run date",
        width: 130,
        renderCell: (p) => fmtDate(p.row.createDate),
      },
      {
        field: "year_end",
        headerName: "Year end",
        width: 130,
        renderCell: (p) => fmtDate(p.row.year_end),
      },
      {
        field: "used_months",
        headerName: "Used months",
        width: 120,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "months_left_to_use",
        headerName: "Months left",
        width: 120,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "residual_value",
        headerName: "Residual (N$)",
        width: 140,
        renderCell: (p) => moneyCell(p.row.residual_value, ui.textPrimary),
      },
      {
        field: "depriciable",
        headerName: "Depreciable (N$)",
        width: 150,
        renderCell: (p) => moneyCell(p.row.depriciable, ui.textPrimary),
      },
      {
        field: "depriciable_ammount",
        headerName: "Monthly depr (N$)",
        width: 160,
        renderCell: (p) =>
          moneyCell(p.row.depriciable_ammount, colors.orange[500]),
      },
      {
        field: "depreciation",
        headerName: "Depreciation (N$)",
        width: 160,
        renderCell: (p) => moneyCell(p.row.depreciation, colors.orange[500]),
      },
      {
        field: "accumulated_depr",
        headerName: "Accumulated depr (N$)",
        width: 180,
        renderCell: (p) => moneyCell(p.row.accumulated_depr, colors.indigo[500]),
      },
      {
        field: "netbook_value",
        headerName: "Net book value (N$)",
        width: 170,
        renderCell: (p) => moneyCell(p.row.netbook_value, colors.green[500]),
      },
      {
        field: "difference",
        headerName: "Difference (N$)",
        width: 160,
        renderCell: (p) =>
          p.row.flagged ? (
            <Tooltip title="Difference is not zero — this line needs auditing">
              <Chip
                size="small"
                label={num(p.row.difference).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                })}
                sx={{
                  height: 22,
                  fontWeight: 800,
                  fontVariantNumeric: "tabular-nums",
                  color: "#FFFFFF",
                  bgcolor: NEEDS_AUDIT_COLOR,
                }}
              />
            </Tooltip>
          ) : (
            moneyCell(p.row.difference, ui.textSecondary)
          ),
      },
      {
        field: "actions",
        headerName: "",
        width: 70,
        sortable: false,
        filterable: false,
        align: "center",
        renderCell: (params) => (
          <Tooltip title="View audit detail">
            <IconButton
              size="small"
              onClick={() => setSelectedRow(params.row)}
              sx={{ color: ui.accent2 }}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ui, colors]
  );

  const NoDataOverlay = () => (
    <Stack alignItems="center" justifyContent="center" height="100%" spacing={1}>
      <img src={Nodata} alt="No data" style={{ width: 140 }} />
      <Typography variant="h5" sx={{ fontWeight: 700, color: ui.textPrimary }}>
        No audit records found
      </Typography>
      <Typography variant="h6" sx={{ color: ui.textSecondary }}>
        {search || activeFilterCount
          ? "Nothing matches the current filters."
          : "No FAR audit records have been captured yet."}
      </Typography>
    </Stack>
  );

  /* ------------------------------------------------------------------ */

  return (
    <Box sx={{ bgcolor: ui.pageBg, minHeight: "100%", pb: 4 }}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <Container maxWidth={false} sx={{ py: 3.5 }}>
        {/* ------------------------------------------------------------ */}
        {/* Header                                                        */}
        {/* ------------------------------------------------------------ */}
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                letterSpacing: -0.4,
                color: ui.textPrimary,
              }}
            >
              FAR Audit
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
              Depreciation schedule and variances against the accounting records
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              size="small"
              placeholder="Search SKU, description, category…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ ...fieldSx, minWidth: { sm: 280 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 19, color: ui.textSecondary }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Exports the filtered rows, grouped by depreciation month">
              <span>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={exportToExcel}
                  sx={primaryBtn}
                >
                  Export by month
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* ------------------------------------------------------------ */}
        {/* KPI cards                                                     */}
        {/* ------------------------------------------------------------ */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard
              ui={ui}
              tone={ui.accent2}
              icon={<FactCheckOutlinedIcon sx={{ fontSize: 18 }} />}
              label="Audit records"
              value={totals.records.toLocaleString()}
              sub={`Across ${totals.months} depreciation month(s)`}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard
              ui={ui}
              tone={colors.orange[500]}
              icon={<TrendingDownOutlinedIcon sx={{ fontSize: 18 }} />}
              label="Total depreciation"
              value={compactMoney(totals.depreciation)}
              sub={`${compactMoney(totals.monthlyDepreciation)} per month`}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard
              ui={ui}
              tone={colors.green[500]}
              icon={<AccountBalanceOutlinedIcon sx={{ fontSize: 18 }} />}
              label="Net book value"
              value={compactMoney(totals.netbook)}
              sub={`Accumulated ${compactMoney(totals.accumulated)}`}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard
              ui={ui}
              tone={totals.flagged ? NEEDS_AUDIT_COLOR : colors.green[500]}
              icon={<ReportProblemOutlinedIcon sx={{ fontSize: 18 }} />}
              label="Needs audit"
              value={totals.flagged.toLocaleString()}
              sub={
                totals.flagged
                  ? `Net ${compactMoney(totals.difference)} unreconciled`
                  : "Every line reconciles"
              }
            />
          </Grid>
        </Grid>

        {/* ------------------------------------------------------------ */}
        {/* Filters                                                       */}
        {/* ------------------------------------------------------------ */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2.5,
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            sx={{ mb: 1.75 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <FilterAltOutlinedIcon sx={{ fontSize: 18, color: ui.accent }} />
              <Typography
                variant="h6"
                sx={{
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: ui.accent,
                }}
              >
                Filters
              </Typography>
              {activeFilterCount > 0 && (
                <Chip
                  size="small"
                  label={`${activeFilterCount} active`}
                  sx={{
                    height: 20,
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    color: ui.accent,
                    bgcolor: alpha(ui.accent, 0.14),
                  }}
                />
              )}
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={varianceFilter}
                onChange={(_, v) => v !== null && setVarianceFilter(v)}
                sx={{
                  gap: 1,
                  "& .MuiToggleButton-root": {
                    px: 1.75,
                    py: 0.6,
                    border: `1px solid ${ui.border} !important`,
                    borderRadius: "999px !important",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 12.5,
                    color: ui.textSecondary,
                    bgcolor: ui.surfaceAlt,
                    "&.Mui-selected": {
                      color: ui.accent,
                      bgcolor: alpha(ui.accent, 0.14),
                      borderColor: `${alpha(ui.accent, 0.5)} !important`,
                    },
                  },
                }}
              >
                <ToggleButton value={ALL}>All</ToggleButton>
                <ToggleButton value="flagged">Needs audit</ToggleButton>
                <ToggleButton value="clean">Reconciled</ToggleButton>
              </ToggleButtonGroup>

              {activeFilterCount > 0 && (
                <Button
                  size="small"
                  startIcon={<CloseIcon sx={{ fontSize: 15 }} />}
                  onClick={clearFilters}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    color: ui.textSecondary,
                    "&:hover": { color: colors.red[500] },
                  }}
                >
                  Clear all
                </Button>
              )}
            </Stack>
          </Stack>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Schedule year"
                value={yearFilter}
                onChange={(e) => setYearFilter(Number(e.target.value))}
                helperText="Drives the 12-month export"
                sx={fieldSx}
              >
                {filterOptions.years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Depreciation month"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                sx={fieldSx}
              >
                <MenuItem value={ALL}>All months</MenuItem>
                {filterOptions.months.map((m) => (
                  <MenuItem key={m} value={m}>
                    {monthLabel(m)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                sx={fieldSx}
              >
                <MenuItem value={ALL}>All categories</MenuItem>
                {filterOptions.categories.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Year end"
                value={yearEndFilter}
                onChange={(e) => setYearEndFilter(e.target.value)}
                sx={fieldSx}
              >
                <MenuItem value={ALL}>All year ends</MenuItem>
                {filterOptions.yearEnds.map((y) => (
                  <MenuItem key={y} value={y}>
                    {fmtDate(y)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mt: 1.75, color: ui.textSecondary }}>
            Showing {visibleRows.length} of {rows.length} audit record(s)
            {totals.flagged > 0 && ` · ${totals.flagged} with a variance`}
          </Typography>
        </Paper>

        {/* ------------------------------------------------------------ */}
        {/* Grid                                                          */}
        {/* ------------------------------------------------------------ */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            boxShadow: ui.shadow,
            height: "calc(100vh - 340px)",
            minHeight: 420,
          }}
        >
          <DataGrid
            rows={visibleRows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            density="comfortable"
            disableRowSelectionOnClick
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pageSizeOptions={[25, 50, 100]}
            getRowClassName={(params) =>
              params.row.flagged ? "row--flagged" : ""
            }
            sx={{
              border: "none",
              color: ui.textPrimary,
              // Matches the export: a non-zero difference needs auditing.
              "& .row--flagged": {
                bgcolor: alpha(NEEDS_AUDIT_COLOR, isDark ? 0.22 : 0.14),
                borderLeft: `3px solid ${NEEDS_AUDIT_COLOR}`,
                "&:hover": { bgcolor: alpha(NEEDS_AUDIT_COLOR, 0.28) },
              },
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: ui.surfaceAlt,
                borderBottom: `1px solid ${ui.border}`,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                fontSize: 11,
                color: ui.textSecondary,
              },
              "& .MuiDataGrid-cell": {
                borderBottom: `1px solid ${alpha(ui.border, 0.7)}`,
              },
              "& .MuiDataGrid-row:hover": { bgcolor: alpha(ui.accent, 0.07) },
              "& .MuiDataGrid-footerContainer": {
                bgcolor: ui.surfaceAlt,
                borderTop: `1px solid ${ui.border}`,
              },
              "& .MuiTablePagination-root": { color: ui.textSecondary },
            }}
            slots={{
              toolbar: () => (
                <Box sx={{ p: 1 }}>
                  <GridToolbarQuickFilter />
                </Box>
              ),
              noRowsOverlay: NoDataOverlay,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 400, placeholder: "Filter…" },
              },
            }}
          />
        </Paper>
      </Container>

      {/* -------------------------------------------------------------- */}
      {/* Detail dialog                                                   */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        {selectedRow && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: 800, color: ui.textPrimary }}
                  >
                    {selectedRow.sku}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ mt: 0.5, color: ui.textSecondary }}
                  >
                    {selectedRow.description || "No description"}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 1, flexWrap: "wrap", gap: 0.75 }}
                  >
                    <Chip
                      size="small"
                      icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 14 }} />}
                      label={selectedRow.monthName}
                      sx={{
                        fontWeight: 700,
                        color: ui.accent,
                        bgcolor: alpha(ui.accent, 0.14),
                        "& .MuiChip-icon": { color: "inherit" },
                      }}
                    />
                    <Chip
                      size="small"
                      label={selectedRow.category}
                      sx={{
                        fontWeight: 700,
                        color: ui.textSecondary,
                        bgcolor: alpha(ui.textSecondary, 0.14),
                      }}
                    />
                    <Chip
                      size="small"
                      label={
                        selectedRow.flagged ? "Needs audit" : "Reconciled"
                      }
                      sx={{
                        fontWeight: 700,
                        color: selectedRow.flagged
                          ? NEEDS_AUDIT_COLOR
                          : colors.green[500],
                        bgcolor: alpha(
                          selectedRow.flagged
                            ? NEEDS_AUDIT_COLOR
                            : colors.green[500],
                          0.18
                        ),
                      }}
                    />
                  </Stack>
                </Box>
                <IconButton
                  onClick={() => setSelectedRow(null)}
                  sx={{ color: ui.textSecondary }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: ui.border }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1.5,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: ui.accent,
                    }}
                  >
                    Period
                  </Typography>
                  <DetailRow
                    ui={ui}
                    label="Depreciation month"
                    value={selectedRow.monthName}
                  />
                  <DetailRow
                    ui={ui}
                    label="Depreciation run date"
                    value={fmtDate(selectedRow.createDate)}
                  />
                  <DetailRow
                    ui={ui}
                    label="Year end"
                    value={fmtDate(selectedRow.year_end)}
                  />
                  <DetailRow
                    ui={ui}
                    label="Used months"
                    value={selectedRow.used_months}
                  />
                  <DetailRow
                    ui={ui}
                    label="Months left to use"
                    value={selectedRow.months_left_to_use}
                  />
                  <DetailRow
                    ui={ui}
                    label="Location"
                    value={selectedRow.location}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1.5,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: ui.accent,
                    }}
                  >
                    Valuation
                  </Typography>
                  <DetailRow
                    ui={ui}
                    label="Cost price"
                    value={money(selectedRow.costprice)}
                  />
                  <DetailRow
                    ui={ui}
                    label="Residual value"
                    value={money(selectedRow.residual_value)}
                  />
                  <DetailRow
                    ui={ui}
                    label="Depreciable amount"
                    value={money(selectedRow.depriciable)}
                  />
                  <DetailRow
                    ui={ui}
                    label="Monthly depreciation"
                    value={money(selectedRow.depriciable_ammount)}
                  />
                  <DetailRow
                    ui={ui}
                    label="Depreciation"
                    value={money(selectedRow.depreciation)}
                    color={colors.orange[500]}
                  />
                  <DetailRow
                    ui={ui}
                    label="Accumulated depreciation"
                    value={money(selectedRow.accumulated_depr)}
                    color={colors.indigo[500]}
                  />
                  <DetailRow
                    ui={ui}
                    label="Net book value"
                    value={money(selectedRow.netbook_value)}
                    color={colors.green[500]}
                  />
                  <DetailRow
                    ui={ui}
                    label="Difference"
                    value={money(selectedRow.difference)}
                    color={
                      selectedRow.flagged ? NEEDS_AUDIT_COLOR : ui.textPrimary
                    }
                  />
                </Grid>
              </Grid>

              {!selectedRow._asset && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(colors.orange[500], 0.45)}`,
                    bgcolor: alpha(colors.orange[500], 0.1),
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: colors.orange[500] }}
                  >
                    Orphaned audit record
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ mt: 0.5, color: ui.textSecondary }}
                  >
                    Asset #{selectedRow.asset_id} is not in the capitalised
                    register — it may have been deleted, or written off as
                    discarded, damaged or refurbished.
                  </Typography>
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2.5 }}>
              <Button
                onClick={() => setSelectedRow(null)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: ui.textSecondary,
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AssetsAudit;
